import { ButtonType, type PathParams } from "@constellation-academy/epic-ui";
import { Accordion, AccordionTab } from "primereact/accordion";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { Suspense, useMemo, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "@i18n/hooks";
import { type orderEditScreen_OrderQuery } from "@relay/orderEditScreen_OrderQuery.graphql";
import { QUERY } from "./order-edit.graphql";
import {
	buttonWrapperClass,
	guestCustomerIdsClass,
	guestNoticeClass,
	headerClass,
	titleRowClass,
} from "./order-edit.styles";
import { BillingDetailsForm } from "./parts/billing-details-form/billing-details-form.component";
import { CartForm } from "./parts/cart-form/cart-form.component";
import { InvoiceDataForm } from "./parts/invoice-data-form/invoice-data-form.component";
import { isUnclaimedGuestOrder } from "@features/orders/unclaimed-guest-order";
import { CloseOrderAction } from "./parts/close-order-action";
import { EraseAttributionAction } from "./parts/erase-attribution-action";
import { InvoiceEmailActions } from "./parts/invoice-email-actions";
import { BaseScreen } from "../../components/base-screen";
import { Button } from "../../components/button";
import { NoAccess } from "../../components/no-access";
import { OrderHistoryTable } from "../../features/orders/order-history-table/order-history-table.component";
import { PaymentDataForm } from "../../features/orders/payment-data-form/payment-data-form.component";
import { useHasPermissions } from "../../hooks/use-has-permissions";
import { Paths } from "../../routes/paths";
import { H2Span } from "../../themes/font-tags";
import { removeGraphqlPrefixFromId } from "../../util/id-converter.utils";
import { type OrdersPath } from "../orders";

export const OrderEditScreen = () => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const { orderId } = useParams<PathParams<typeof OrdersPath>>();

	const [orderRefetchKey, setOrderRefetchKey] = useState(0);
	const query = useLazyLoadQuery<orderEditScreen_OrderQuery>(
		QUERY,
		{
			id: orderId!,
			skip: !orderId,
		},
		{ fetchKey: orderRefetchKey, fetchPolicy: "store-and-network" },
	);

	const canReadAndModify = useHasPermissions([
		"UserInAccountPermission_OrderAdmin_Read",
		"UserInAccountPermission_OrderAdmin_Modify",
	]);

	const hasPurchaser = !!query.node?.purchaser;
	const isGuestOrder = !!query.node && isUnclaimedGuestOrder(query.node);
	const isClaimedGuestOrder = hasPurchaser && !!query.node?.startedAsGuest;
	const isAwaitingAccount = query.node?.status === "PurchasedAwaitingAccount";
	const { userId, accountId } = query.node?.purchaser ?? {};
	const guestCustomerIds = query.node?.guestCustomerIds;

	const convertedId = useMemo(() => {
		return query.node?.id ? atob(removeGraphqlPrefixFromId(query.node.id)) : t("order.unknown");
	}, [query.node?.id, t]);

	const navigateToUser = () => {
		if (!userId) return;

		navigate(Paths.users.withId(userId).edit.path);
	};

	const navigateToAccount = () => {
		if (!accountId) return;

		navigate(Paths.accounts.withId(accountId).edit.path);
	};

	const navigateBack = () => {
		navigate(-1);
	};

	if (!canReadAndModify) return <NoAccess />;

	return query.node ? (
		<Suspense fallback={<ProgressSpinner />}>
			<BaseScreen
				title={
					<div className={titleRowClass}>
						<span>{t("order.title", { id: convertedId })}</span>
						<Button
							type={ButtonType.Button}
							label={t("actions.back")}
							onClick={navigateBack}
						/>
					</div>
				}
			>
				<header className={headerClass}>
					<div>
						<H2Span>{t("order.buyerInfo")}</H2Span>
						<div className={buttonWrapperClass}>
							<Button
								type={ButtonType.Button}
								label={
									isClaimedGuestOrder
										? t("order.toClaimingAccount")
										: t("order.toAccount")
								}
								onClick={navigateToAccount}
								disabled={!hasPurchaser}
							/>
							<Button
								type={ButtonType.Button}
								label={
									isClaimedGuestOrder
										? t("order.toClaimingUser")
										: t("order.toUser")
								}
								onClick={navigateToUser}
								disabled={!hasPurchaser}
							/>
						</div>
						{isGuestOrder && (
							<div className={guestNoticeClass}>{t("order.guestOrderNotice")}</div>
						)}
						{isGuestOrder && guestCustomerIds && (
							<div className={guestCustomerIdsClass}>
								{guestCustomerIds.stripeCustomerId && (
									<div>
										{t("order.stripeCustomerId")}:{" "}
										{guestCustomerIds.stripeCustomerId}
									</div>
								)}
								{guestCustomerIds.invoicingCustomerId && (
									<div>
										{t("order.invoicingCustomerId")}:{" "}
										{guestCustomerIds.invoicingCustomerId}
									</div>
								)}
							</div>
						)}
					</div>
				</header>
				<Accordion activeIndex={0}>
					<AccordionTab header={t("order.customerDetails")} tabIndex={0}>
						{query.node?.customerDetails && (
							<BillingDetailsForm
								billingDetailsFragmentRef={query.node?.customerDetails}
							/>
						)}
					</AccordionTab>
					<AccordionTab header={t("order.invoiceEmailTab")} tabIndex={1}>
						<InvoiceEmailActions
							orderFragmentRef={query.node}
							onHistoryChanged={() => setOrderRefetchKey((key) => key + 1)}
						/>
					</AccordionTab>
					{isAwaitingAccount && (
						<AccordionTab header={t("order.closeOrderTab")} tabIndex={2}>
							<CloseOrderAction orderFragmentRef={query.node} />
						</AccordionTab>
					)}
					{!hasPurchaser && (
						<AccordionTab header={t("order.eraseAttributionTab")} tabIndex={3}>
							<EraseAttributionAction orderFragmentRef={query.node} />
						</AccordionTab>
					)}
					<AccordionTab header={t("order.cart")} tabIndex={4}>
						<CartForm orderFragmentRef={query.node} />
					</AccordionTab>
					<AccordionTab header={t("order.paymentData")} tabIndex={5}>
						<PaymentDataForm orderFragmentRef={query.node} />
					</AccordionTab>
					<AccordionTab header={t("order.invoiceDataTab")} tabIndex={6}>
						<InvoiceDataForm orderFragmentRef={query.node} />
					</AccordionTab>
					<AccordionTab header={t("order.history")} tabIndex={7}>
						<OrderHistoryTable
							orderHistoriesFragmentRef={query}
							hasPurchaser={hasPurchaser}
						/>
					</AccordionTab>
				</Accordion>
			</BaseScreen>
		</Suspense>
	) : (
		<h4>{t("order.loadError")}</h4>
	);
};
